import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../../context/GameContext';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSmile, FiFrown } = FiIcons;

const SCENES = [
  {
    id: 1,
    title: "The Swing Set",
    video: "🎬",
    scene: "Teddy is waiting in line for the swing. Another kid cuts in front of him. Teddy pushes the kid off the swing.",
    isRight: false,
    explanation: "Pushing hurts others. Teddy should use words or ask a grown-up for help instead.",
    feedback: "❌ Oops! Pushing is never the answer."
  },
  {
    id: 2,
    title: "Snack Time",
    video: "🥪",
    scene: "Teddy's friend looks sad. Teddy shares his cookies with them.",
    isRight: true,
    explanation: "Sharing shows kindness! When we share, we make friends feel happy and loved.",
    feedback: "✨ Great job! Sharing is caring!"
  },
  {
    id: 3,
    title: "The Toy Truck",
    video: "🚚",
    scene: "Teddy sees a cool toy truck that another kid is playing with. Teddy takes it without asking.",
    isRight: false,
    explanation: "Taking without asking isn't fair. Teddy should ask 'Can I play when you're done?'",
    feedback: "❌ Not quite. We should always ask first."
  },
  {
    id: 4,
    title: "Helping a Friend",
    video: "🤝",
    scene: "A kid falls down and scrapes their knee. Teddy helps them up and walks with them to get a bandage.",
    isRight: true,
    explanation: "Helping others when they're hurt shows big kindness! You're a wonderful friend.",
    feedback: "🌟 Amazing! Helping others is so kind."
  },
  {
    id: 5,
    title: "Taking Turns",
    video: "🎨",
    scene: "Teddy and friends are painting. Teddy keeps all the blue paint and won't let others use it.",
    isRight: false,
    explanation: "Sharing supplies means everyone gets to create. Taking turns makes art time fun for all!",
    feedback: "❌ Remember to share art supplies."
  },
  {
    id: 6,
    title: "Listening to Teacher",
    video: "👩‍🏫",
    scene: "The teacher asks everyone to clean up. Teddy keeps playing and ignores her.",
    isRight: false,
    explanation: "Listening to teachers keeps us safe and helps the class run smoothly. Good listening is important!",
    feedback: "❌ It's important to listen to teachers."
  },
  {
    id: 7,
    title: "Including Everyone",
    video: "⚽",
    scene: "Kids are choosing teams for soccer. Teddy asks the new kid to be on his team.",
    isRight: true,
    explanation: "Including everyone makes sure no one feels left out. You're building a friendly playground!",
    feedback: "✨ Wonderful! Including everyone is so kind."
  },
  {
    id: 8,
    title: "Using Kind Words",
    video: "💬",
    scene: "Teddy's friend draws a picture. Teddy says 'That's the worst drawing I've ever seen!'",
    isRight: false,
    explanation: "Words can hurt feelings. We can say what we like about the picture or keep unkind thoughts to ourselves.",
    feedback: "❌ Words can hurt. Let's be kind with what we say."
  }
];

export default function SELTeddyPlayground() {
  const navigate = useNavigate();
  const { dispatch } = useGame();
  const [currentScene, setCurrentScene] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [kindnessStars, setKindnessStars] = useState(0);
  const [completed, setCompleted] = useState(false);

  const scene = SCENES[currentScene];

  const handleAnswer = (isRight) => {
    setSelectedAnswer(isRight);
    setShowFeedback(true);
    
    if (isRight === scene.isRight) {
      setKindnessStars(s => s + 1);
      dispatch({ type: 'ADD_SCORE', payload: 10 });
    }
  };

  const nextScene = () => {
    if (currentScene < SCENES.length - 1) {
      setCurrentScene(c => c + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    } else {
      setCompleted(true);
      dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'sel-teddy-playground', bonusTokens: kindnessStars * 5 + 10 } });
    }
  };

  if (completed) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-rose-100 to-pink-100">
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-rose-200 text-center max-w-md">
          <motion.div animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="text-6xl mb-4">🧸</motion.div>
          <h2 className="text-3xl font-kid text-rose-700 mb-4">Playground Champion!</h2>
          <div className="bg-rose-50 rounded-2xl p-4 mb-6">
            <p className="font-comic text-rose-600">You earned {kindnessStars} Kindness Stars! ⭐</p>
            <p className="font-comic text-sm text-rose-500 mt-2">Teddy is so proud of you!</p>
          </div>
          <div className="space-y-3">
            <button onClick={() => window.location.reload()} className="w-full bg-rose-500 text-white p-3 rounded-2xl font-kid">Play Again</button>
            <button onClick={() => navigate('/dashboard')} className="w-full bg-gray-200 text-gray-700 p-3 rounded-2xl font-kid">Back to Dashboard</button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-4 pb-24 bg-gradient-to-br from-rose-50 to-pink-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 bg-white rounded-2xl px-4 py-2 shadow border-2 border-rose-200">
            <SafeIcon icon={FiArrowLeft} /> Back
          </button>
          <div className="bg-white rounded-2xl px-4 py-2 shadow border-2 border-rose-200">
            <span className="font-kid text-rose-700">Scene {currentScene + 1} of {SCENES.length}</span>
          </div>
          <div className="bg-yellow-100 rounded-2xl px-4 py-2 shadow border-2 border-yellow-300">
            <span className="font-kid text-yellow-700">⭐ {kindnessStars}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-4 bg-rose-100 rounded-full mb-8 overflow-hidden border-2 border-rose-200">
          <motion.div 
            animate={{ width: `${((currentScene + 1) / SCENES.length) * 100}%` }}
            className="h-full bg-gradient-to-r from-rose-400 to-pink-500 rounded-full"
          />
        </div>

        {/* Scene Card */}
        <motion.div 
          key={scene.id}
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-rose-200 mb-8"
        >
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">{scene.video}</div>
            <h2 className="font-kid text-2xl text-rose-700 mb-2">{scene.title}</h2>
          </div>

          <div className="bg-rose-50 rounded-2xl p-6 mb-8 border-2 border-rose-100">
            <p className="font-comic text-lg text-gray-700 leading-relaxed text-center">
              {scene.scene}
            </p>
          </div>

          {!showFeedback ? (
            <div className="grid grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleAnswer(true)}
                className="bg-green-100 border-4 border-green-300 rounded-2xl p-6 text-center hover:bg-green-200 transition-colors"
              >
                <div className="text-4xl mb-2">😊</div>
                <span className="font-kid text-green-700">Right Choice!</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleAnswer(false)}
                className="bg-red-100 border-4 border-red-300 rounded-2xl p-6 text-center hover:bg-red-200 transition-colors"
              >
                <div className="text-4xl mb-2">😟</div>
                <span className="font-kid text-red-700">Wrong Choice</span>
              </motion.button>
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-6 text-center border-4 ${selectedAnswer === scene.isRight ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}
            >
              <div className="text-4xl mb-3">{selectedAnswer === scene.isRight ? '✨' : '💡'}</div>
              <p className="font-kid text-xl mb-2 text-gray-800">{scene.feedback}</p>
              <p className="font-comic text-gray-600 mb-4">{scene.explanation}</p>
              <button 
                onClick={nextScene}
                className="bg-rose-500 text-white px-6 py-3 rounded-2xl font-kid hover:bg-rose-600 transition-colors"
              >
                {currentScene < SCENES.length - 1 ? 'Next Scene →' : 'Finish! 🎉'}
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}